import { download, generateCsv, mkConfig } from "export-to-csv"
import { useCallback } from "react"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../localization/useDateTime"
import { CompaniesUsersItem } from "../../../../../users/userTypes"

type UseExportUsersParams = Readonly<{
    data: ReadonlyArray<CompaniesUsersItem> | null | undefined
}>

export function useExportUsers({ data }: UseExportUsersParams) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        filename: "Facilitynet-company-users",
    })

    const mapUserToCsvRow = (user: CompaniesUsersItem) => ({
        [l10n.getString("company-users-export-header-name")]: user.displayName,
        [l10n.getString("company-users-export-header-first-name")]: user.givenName,
        [l10n.getString("company-users-export-header-last-name")]: user.surname,
        [l10n.getString("company-users-export-header-username")]: user.username,
        [l10n.getString("company-users-export-header-email")]: user.email,
        [l10n.getString("company-users-export-header-mobile")]: user.mobileNumber ?? "",
        [l10n.getString("company-users-export-header-phone")]: user.phoneNumber ?? "",
        [l10n.getString("company-users-export-header-employee-id")]: user.employeeId ?? "",
        [l10n.getString("company-users-export-header-user-group")]: user.userGroup?.name ?? "",
        [l10n.getString("company-users-export-header-department")]: user.department?.name ?? "",
        [l10n.getString("company-users-export-header-language")]: user.language,
        [l10n.getString("company-users-export-header-roles")]: (user.roles ?? []).join(", "),
        [l10n.getString("company-users-export-header-user-sync")]: user.company?.companySetup?.userSynchronization
            ? l10n.getString("yes")
            : l10n.getString("no"),
        [l10n.getString("company-users-export-header-last-signin")]: user.lastLoginEvent?.timestamp
            ? dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(user.lastLoginEvent.timestamp))
            : "",
        [l10n.getString("company-users-export-header-status")]: user.isActive
            ? l10n.getString("company-users-status-active")
            : l10n.getString("company-users-status-inactive"),
    })

    return useCallback(() => {
        const exportRows = data && data.length > 0 ? data.map(mapUserToCsvRow) : []

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }, [dateTimeFactory, l10n, data])
}
